import React, {Fragment, useEffect, useState} from 'react';
import {Checkbox, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup} from '@mui/material';
import Divider from '@mui/material/Divider';
import makeStyles from '@mui/styles/makeStyles';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import Formatter from '../../../util/formatter';
import {user} from '../../../store/reducers/user';
import Notes from '../../../util/notes';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';

const useStyles = makeStyles((theme) => ({

    divider: {
        width: '100%',
        margin: '5px',
    },

    blockSwitch: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    textField: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    provideDetailsLabel: {
        alignItems: 'center',
    },

    radio: {
        width: '100%',
        minWidth: '100%',
    },

    mandatory: {
        fontWeight: 'bold',
        color: '#e06666',
    },
    clearButton: {
        border: '1px solid #2180f7',
        height: '25px',
        placeSelf: 'center',
    },
    container: {
        padding: '15px',
    },
    title: {
        marginBottom: '24px',
        fontWeight: 600,
        textAlign: 'center',
    }

}));



const CardiacStrokeAmbForm = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Selectors
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [cardiacStrokeAmb, setCardiacStrokeAmb] = useState({
        timeOfOnset: null,
        timeOfEcg: null,
        fourLeadRhythms: '', //dropdown
        twelveLeadRhythms: '', //dropdown
        otherRhythm: '',
        stemi: false,
        padsApplied: false,
        referredToPciCentre: false,
    });

    useEffect(() => {
        if (props.patient?.studies?.cardiacStrokeAmb) {
            setCardiacStrokeAmb(props.patient?.studies?.cardiacStrokeAmb);
        }
    }, []);


    useEffect(() => {
        if (props.onDisableSubmit) {
            props.onDisableSubmit(isSubmitDisabled())
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSaveForm);
        }
    }, [cardiacStrokeAmb, userObject]);

    const handleChangeTextField = (event) => {
        const {name, value} = event.target;
        setCardiacStrokeAmb(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeBlockSwitch = (name) => {
        setCardiacStrokeAmb(pr => ({
            ...pr,
            [name]: !pr[name],
        }));
    };

    const isSubmitDisabled = () => {
        return Object.values(cardiacStrokeAmb).some(
            value => value == null || value === ''
        );
    };

    const handleSaveForm = () => {
        const objectToSave = {
            ...cardiacStrokeAmb,
            timeStamp: moment().toISOString(),
            by: tester,
        };
        let dataArray = [{
            name: 'studies.cardiacStrokeAmb',
            value: objectToSave,
        }];

        props.onChange(dataArray);
    };

    return (
        <>
            <Typography variant="h5" className={classes.title}>
                Cardiac and Stroke Assessment
            </Typography>

            <Grid container spacing={2} className={classes.container}>
                <Grid item xs={6}>
                    <FormLabel className={classes.label}>
                        {t('Date and time of onset')}
                    </FormLabel>
                </Grid>
                <Grid item xs={6}>
                    <AgyleDatePicker
                        type="dateTime"
                        error={!cardiacStrokeAmb.timeOfOnset}
                        value={cardiacStrokeAmb.timeOfOnset}
                        onChange={(param) => {setCardiacStrokeAmb((pr) =>
                            ({
                                ...pr,
                                timeOfOnset: param
                            }))}}
                        noBorder={false}
                        withNewBackground
                        maxDate={moment()}
                    />
                </Grid>

                <Grid item xs={6}>
                    <FormLabel className={classes.label}>
                        {t('Date and time of ECG')}
                    </FormLabel>
                </Grid>
                <Grid item xs={6}>
                    <AgyleDatePicker
                        type="dateTime"
                        error={!cardiacStrokeAmb.timeOfEcg}
                        value={cardiacStrokeAmb.timeOfEcg}
                        onChange={(param) => {setCardiacStrokeAmb((pr) =>
                            ({
                                ...pr,
                                timeOfEcg: param
                            }))}}
                        noBorder={false}
                        withNewBackground
                        maxDate={moment()}
                    />
                </Grid>

                {/*dropdown*/}
                <Grid item xs={6}>
                    <FormLabel className={classes.label}>
                        {t('4-lead rhythms')}
                    </FormLabel>
                </Grid>
                <Grid item xs={6}>
                    <AgyleTextField
                        fullWidth
                        name="fourLeadRhythms"
                        maxRows={25}
                        multiline
                        onChange={handleChangeTextField}
                        value={cardiacStrokeAmb.fourLeadRhythms}
                        variant="outlined"
                        error={!cardiacStrokeAmb.fourLeadRhythms}
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>


                {/*dropdown*/}
                <Grid item xs={6}>
                    <FormLabel className={classes.label}>
                        {t('12-lead rhythms')}
                    </FormLabel>
                </Grid>
                <Grid item xs={6}>
                    <AgyleTextField
                        fullWidth
                        name="twelveLeadRhythms"
                        maxRows={25}
                        multiline
                        onChange={handleChangeTextField}
                        value={cardiacStrokeAmb.twelveLeadRhythms}
                        variant="outlined"
                        error={!cardiacStrokeAmb.twelveLeadRhythms}
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={6}>
                    <FormLabel className={classes.label}>
                        {t('Other rhythm')}
                    </FormLabel>
                </Grid>
                <Grid item xs={6}>
                    <AgyleTextField
                        fullWidth
                        name="otherRhythm"
                        maxRows={25}
                        multiline
                        onChange={handleChangeTextField}
                        value={cardiacStrokeAmb.otherRhythm}
                        variant="outlined"
                        error={!cardiacStrokeAmb.otherRhythm}
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        wrap="nowrap"
                    >
                        <Grid item xs={6}>
                            <FormLabel>
                                {t('STEMI')}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={6} className={classes.blockSwitch}>
                            <AgyleBlockSwitch
                                value={cardiacStrokeAmb.stemi}
                                onChange={() => handleChangeBlockSwitch('stemi')}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        wrap="nowrap"
                    >
                        <Grid item xs={6}>
                            <FormLabel>
                                {t('Pads applied')}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={6} className={classes.blockSwitch}>
                            <AgyleBlockSwitch
                                value={cardiacStrokeAmb.padsApplied}
                                onChange={() => handleChangeBlockSwitch('padsApplied')}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        wrap="nowrap"
                    >
                        <Grid item xs={6}>
                            <FormLabel>
                                {t('Referred to PCI centre')}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={6} className={classes.blockSwitch}>
                            <AgyleBlockSwitch
                                value={cardiacStrokeAmb.referredToPciCentre}
                                onChange={() => handleChangeBlockSwitch('referredToPciCentre')}
                            />
                        </Grid>
                    </Grid>
                </Grid>

            </Grid>
        </>
    );
};

export default CardiacStrokeAmbForm;